import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { categories } from "@/utils/movie";

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 免费在线电影",
    description: "了解免费在线电影网站定位、服务内容、团队理念与版权免责声明，我们致力于为影迷提供便捷的高清电影信息浏览体验。",
    keywords: "关于我们,免费在线电影,影视内容平台,版权声明,免责声明",
  });

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-primary-950">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-100/80" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">关于我们</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">关于我们</h1>
          <p className="mt-3 max-w-2xl text-sm text-background-100/80 md:text-base">
            一个为影迷而生的高清电影信息浏览平台，让找片、看片变得更简单。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-4xl px-4 py-12 md:px-6">
        <section>
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            网站定位与服务介绍
          </h2>
          <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
            <strong>免费在线电影</strong>专注于提供高清电影资源的在线观看入口与影片信息浏览服务。
            我们汇聚动作、剧情、喜剧、科幻、爱情、恐怖等十大主流类型影片，通过清晰的内容分类、
            便捷的搜索筛选与实时更新的推荐榜单，帮助广大影迷快速找到感兴趣的作品。
          </p>
          <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
            我们的目标是打造一个<strong>免费、便捷、内容丰富的观影信息平台</strong>，让每一位普通观众都能在手机或电脑上
            轻松浏览影片资料、了解剧情简介与演职人员，享受愉悦的影视浏览体验。
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { icon: "ri-movie-2-line", value: "5000+", label: "影片资料" },
              { icon: "ri-grid-line", value: "10 类", label: "电影类型" },
              { icon: "ri-search-line", value: "全站搜索", label: "快速找片" },
              { icon: "ri-smartphone-line", value: "多端适配", label: "手机电脑" },
            ].map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                <i className={`${item.icon} text-2xl text-accent-500`}></i>
                <p className="mt-2 font-heading text-lg font-bold text-foreground-950">{item.value}</p>
                <p className="text-xs text-foreground-500">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            团队介绍
          </h2>
          <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
            我们是一支热爱电影、熟悉互联网产品的年轻团队。团队成员来自内容运营、视觉设计与前端开发等不同领域，
            共同的兴趣让我们走到一起——把好电影推荐给更多人。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { name: "内容运营组", desc: "负责影片资料整理、分类维护与推荐榜单更新。" },
              { name: "视觉设计组", desc: "负责站点视觉风格、海报呈现与阅读体验优化。" },
              { name: "技术开发组", desc: "负责网站功能开发、性能优化与多端适配。" },
            ].map((team) => (
              <div key={team.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-team-line text-xl"></i>
                </span>
                <p className="mt-3 font-heading text-base font-semibold text-foreground-950">{team.name}</p>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">{team.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500"></span>
            探索更多分类
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-4 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-accent-100 hover:text-accent-800"
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}电影
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-shield-check-line text-accent-500"></i>
            版权声明与免责声明
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-loose text-foreground-600">
            <p>1. 本站为影视内容信息浏览演示平台，所有影片资料、图片与剧情简介均用于页面展示与学习交流，不提供任何视频文件的存储与下载。</p>
            <p>2. 站内影片名称、演职人员等信息仅作介绍用途，相关版权归各自权利人所有。如内容涉及版权问题，请通过联系我们页面告知，我们将及时处理。</p>
            <p>3. 站内“在线播放”功能仅为前端界面演示，不含真实视频源与播放能力。</p>
            <p>4. 用户通过本站跳转或获取的任何第三方信息，其内容与准确性由第三方负责，本站不承担由此产生的相关责任。</p>
          </div>
        </section>
      </div>
    </div>
  );
}